import { useEffect, useState } from "react";
import styles from "../../styles/components/MailPreview.module.css";
import Button from "../../ui/buttons/Button.jsx";
import IconButton from "../../ui/buttons/IconButton.jsx";
import Avatar from "../../ui/avatar/Avatar.jsx";
import { ReactComponent as EditIconSvg } from "../../assets/icons/edit.svg";
import { ReactComponent as TrashIcon } from "../../assets/icons/trash.svg";
import { ConfirmDeleteModal } from "../admin/AdminDetailModals.jsx";
import ModalOverlay from "../../ui/Modals/ModalOverlay.jsx";
import { useNavigate } from "react-router-dom";
import Toggle from "../../ui/selectors/Toggle.jsx";
import BooleanStatusIcon from "../../ui/icons/BooleanStatusIcon.jsx";
import LoadingSpinner from "../../ui/spinner/LoadingSpinner.jsx";
import { openMailOAuthPopup } from "../../utils/mailOAuthPopup";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import {
  getMailEnableWarning,
  mailNeedsEnabling,
} from "../../utils/mailEnabling";

const RESPONDED_SEND_NUMBER_TOOLTIP =
  "No se puede actualizar el send number de una cadena respondida.";

function Tag({ children }) {
  return (
    <div
      style={{
        minHeight: 20,
        padding: "0 12px",
        background: "var(--color-brand-600)",
        borderRadius: 100,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        maxWidth: "100%",
      }}
    >
      <Txt
        style={{
          color: "var(--text-cta-primary)",
          fontSize: 13,
          fontWeight: 500,
          lineHeight: "16px",
          whiteSpace: "nowrap",
        }}
      >
        {children}
      </Txt>
    </div>
  );
}

function formatSendErrorDate(dateString) {
  if (!dateString) return null;

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) return null;

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

function getUpdateErrorMessage(error, field) {
  const fieldMessage = error?.response?.data?.[field];
  const message = Array.isArray(fieldMessage)
    ? fieldMessage.join(" ")
    : fieldMessage;

  return message || "No se pudo actualizar el mail.";
}

function Txt({ children, style }) {
  return (
    <div
      style={{
        fontFamily: "DM Sans",
        minWidth: 0,
        wordWrap: "break-word",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function MailPreview({
  onClose,
  data,
  isLoading = false,
  templateGroupId,
  onDeleteMail,
  onUpdateMail,
  onEnableMail,
}) {
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [updatingField, setUpdatingField] = useState(null);
  const [updateError, setUpdateError] = useState("");
  const [sendNumberInput, setSendNumberInput] = useState(
    String(data?.send_number ?? ""),
  );
  const safeHtml = sanitizeHtml(data?.body || "");
  const isChainResponded = Boolean(data?.status_response);
  const canEditSendNumber =
    Boolean(onUpdateMail) && !isChainResponded && data?.send_number != null;
  const isSendNumberDisabled = !canEditSendNumber || updatingField !== null;

  useEffect(() => {
    setSendNumberInput(String(data?.send_number ?? ""));
  }, [data?.id, data?.send_number]);

  const handleDeleteConfirm = async () => {
    await onDeleteMail?.(data?.id);
  };

  const handleEnable = async () => {
    if (!data?.id || !onEnableMail || updatingField) return;

    setUpdatingField("enable");
    setUpdateError("");
    try {
      const enableResult = await onEnableMail(data.id);
      const enableWarning = getMailEnableWarning(enableResult);
      if (enableWarning) setUpdateError(enableWarning);
    } catch {
      setUpdateError("No se pudo habilitar el mail.");
    } finally {
      setUpdatingField(null);
    }
  };

  const handleStatusToggle = async (field, nextValue) => {
    if (!data?.id || !onUpdateMail || updatingField) return;

    setUpdatingField(field);
    setUpdateError("");
    try {
      await onUpdateMail(data.id, { [field]: nextValue });
    } catch (error) {
      setUpdateError(getUpdateErrorMessage(error, field));
    } finally {
      setUpdatingField(null);
    }
  };

  const handleSendNumberCommit = async () => {
    if (!data?.id || !canEditSendNumber || updatingField) return;

    const currentSendNumber = Number(data.send_number);
    const nextSendNumber = Number(sendNumberInput);
    const isValidSendNumber =
      sendNumberInput.trim() !== "" &&
      Number.isInteger(nextSendNumber) &&
      nextSendNumber >= 0;

    if (!isValidSendNumber) {
      setSendNumberInput(String(currentSendNumber));
      return;
    }

    if (nextSendNumber === currentSendNumber) return;

    setUpdatingField("send_number");
    setUpdateError("");
    try {
      await onUpdateMail(data.id, { send_number: nextSendNumber });
    } catch (error) {
      setSendNumberInput(String(currentSendNumber));
      setUpdateError(getUpdateErrorMessage(error, "send_number"));
    } finally {
      setUpdatingField(null);
    }
  };

  const handleSendNumberKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.currentTarget.blur();
    }
  };

  return (
    <ModalOverlay className={styles.ModalOverlay} onClose={onClose}>
      <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
        <div className={styles.modalContent}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <div className={styles.AvatarTagGroup}>
              <Avatar
                src={data?.responsible?.account?.photo ?? data?.responsible?.avatar_url}
                name={data?.responsible?.name}
                size={24}
              />
              {data?.lead?.name && (
                <Txt style={{ fontSize: 14, fontWeight: 500 }}>{data.lead.name}</Txt>
              )}
              {data?.mail_account?.name && <Tag>{data.mail_account.name}</Tag>}
            </div>
            {onDeleteMail && (
              <IconButton
                tone="danger"
                size="md"
                icon={<TrashIcon className={styles.trashIcon} />}
                onClick={() => setShowDeleteConfirm(true)}
                aria-label="Eliminar mail"
              />
            )}
          </div>
          <div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <Txt style={{ color: "var(--text-tertiary)", fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
                  Estado
                </Txt>
                <Toggle
                  checked={Boolean(data?.status)}
                  disabled={!onUpdateMail || updatingField !== null}
                  ariaLabel="Estado"
                  onChange={(nextValue) => handleStatusToggle("status", nextValue)}
                />
              </div>
              <div>
                <Txt style={{ color: "var(--text-tertiary)", fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
                  Respondido
                </Txt>
                <Toggle
                  checked={Boolean(data?.status_response)}
                  disabled={!onUpdateMail || updatingField !== null}
                  ariaLabel="Respondido"
                  onChange={(nextValue) =>
                    handleStatusToggle("status_response", nextValue)
                  }
                />
              </div>
              <div>
                <Txt style={{ color: "var(--text-tertiary)", fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
                  Habilitado
                </Txt>
                {mailNeedsEnabling(data) ? (
                  <Button
                    variant="primary"
                    tone="blue"
                    height={32}
                    loading={updatingField === "enable"}
                    disabled={!onEnableMail || updatingField !== null}
                    onClick={handleEnable}
                  >
                    Habilitar
                  </Button>
                ) : (
                  <BooleanStatusIcon
                    value={data?.rejected == null ? null : true}
                    trueLabel="Habilitado"
                    falseLabel="Rechazado"
                  />
                )}
              </div>
              <div>
                <Txt style={{ color: "var(--text-tertiary)", fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
                  Send number
                </Txt>
                <span
                  title={isChainResponded ? RESPONDED_SEND_NUMBER_TOOLTIP : undefined}
                  style={{ display: "inline-flex" }}
                >
                  <input
                    type="number"
                    min={0}
                    step={1}
                    aria-label="Send number"
                    value={sendNumberInput}
                    disabled={isSendNumberDisabled}
                    onChange={(event) => setSendNumberInput(event.target.value)}
                    onBlur={handleSendNumberCommit}
                    onKeyDown={handleSendNumberKeyDown}
                    style={{
                      width: 72,
                      height: 32,
                      padding: "0 10px",
                      boxSizing: "border-box",
                      borderRadius: 8,
                      border: "1px solid var(--border-primary, rgba(0,0,0,0.1))",
                      background: "transparent",
                      color: "inherit",
                      fontFamily: "DM Sans",
                      fontSize: 13,
                      cursor: isSendNumberDisabled ? "not-allowed" : "text",
                      opacity: isSendNumberDisabled ? 0.6 : 1,
                    }}
                  />
                </span>
              </div>
            </div>
            {updateError && (
              <Txt style={{ color: "var(--text-error)", fontSize: 12, marginTop: 8 }}>
                {updateError}
              </Txt>
            )}
          </div>
          <div>
            <Txt style={{ color: "var(--text-tertiary)", fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
              Asunto
            </Txt>
            <h3 className={styles.title}>{data?.subject}</h3>
          </div>
          {isLoading ? (
            <div
              className={styles.bodyLoading}
              role="status"
              aria-label="Cargando mail"
            >
              <LoadingSpinner size={40} />
            </div>
          ) : (
            <div
              className={styles.body}
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
          )}
          {data?.send_error && (
            <div className={styles.sendErrorBox}>
              <Txt style={{ color: "var(--text-error)", fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
                Error de envío
              </Txt>
              <Txt style={{ fontSize: 13 }}>{data.send_error}</Txt>
              {formatSendErrorDate(data?.send_error_date_approved) && (
                <Txt style={{ color: "var(--text-tertiary)", fontSize: 12, marginTop: 4 }}>
                  Aprobado para envío: {formatSendErrorDate(data.send_error_date_approved)}
                </Txt>
              )}
              {formatSendErrorDate(data?.last_send) && (
                <Txt style={{ color: "var(--text-tertiary)", fontSize: 12, marginTop: 4 }}>
                  Último envío del mail: {formatSendErrorDate(data.last_send)}
                </Txt>
              )}
              {data?.responsible?.needs_reauth && (
                <Button
                  tone="blue"
                  size="md"
                  style={{ marginTop: 12 }}
                  onClick={() =>
                    openMailOAuthPopup({
                      provider: data.responsible.provider || "google",
                      account: data.responsible,
                    })
                  }
                >
                  Reconectar casilla
                </Button>
              )}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: "auto", alignSelf: "center" }}>
            <Button
              tone="blue"
              width={349}
              onClick={() => {
                onClose();
                navigate("/leads/" + data?.lead?.id);
              }}
            >
              Ver Lead
            </Button>
            <Button
              variant="secondary"
              tone="blue"
              width={349}
              iconLeft={<EditIconSvg className={styles.editIcon} />}
              onClick={() => {
                onClose();
                navigate("/templates", {
                  state: { openTemplateGroupId: templateGroupId },
                });
              }}
            >
              Editar Template
            </Button>
          </div>
        </div>
      </div>

      {showDeleteConfirm && (
        <ConfirmDeleteModal
          title="¿Eliminar este mail?"
          description="Esta acción no tiene vuelta atrás. Si este mail está esperando revisión o tiene una comunicación activa, también será eliminado."
          onConfirm={handleDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
        />
      )}
    </ModalOverlay>
  );
}

export default MailPreview;
